import { useState } from 'react';
import { useGameState } from './state/useGameState.ts';
import { Header, type Page } from './components/Header.tsx';
import { QuizView } from './components/QuizView.tsx';
import { StatsView } from './components/StatsView.tsx';
import { LeaderboardView } from './components/LeaderboardView.tsx';
import { HowItWorksView } from './components/HowItWorksView.tsx';
import { ProfileView } from './components/ProfileView.tsx';
import { AuthModal } from './components/AuthModal.tsx';
import { ConnectionLost } from './components/ConnectionLost.tsx';
import { Toasts } from './components/Toasts.tsx';
import { APP_VERSION } from './version.ts';
import { api } from './api.ts';

export default function App(): React.ReactElement {
  const game = useGameState();
  const [page, setPage] = useState<Page>('play');
  const [viewingUser, setViewingUser] = useState<string | null>(null);
  const [authOpen, setAuthOpen] = useState(false);

  const navigate = (p: Page): void => {
    if (p !== 'user-stats') setViewingUser(null);
    setPage(p);
  };
  const showUser = (username: string): void => {
    setViewingUser(username);
    setPage('user-stats');
  };

  return (
    <div className="app">
      <Header
        page={page}
        onNavigate={navigate}
        me={game.state.me}
        participantCount={game.state.participantCount}
        connection={game.state.connection}
        onOpenAuth={() => setAuthOpen(true)}
      />
      <main className="main">
        {page === 'play' && (
          <QuizView
            round={game.state.round}
            reveal={game.state.reveal}
            participantCount={game.state.participantCount}
            answeredCount={game.state.answeredCount}
            lastSubmittedAnswerId={game.state.lastSubmittedAnswerId}
            onSubmit={game.submitAnswer}
            miniLeaderboard={game.state.miniLeaderboard}
            yourMini={game.state.yourMini}
            stats={game.state.me?.stats ?? null}
            onOpenHowItWorks={() => navigate('how')}
            onOpenUser={showUser}
          />
        )}
        {page === 'stats' && <StatsView baseStats={game.state.me?.stats ?? null} />}
        {page === 'leaderboard' && <LeaderboardView onOpenUser={showUser} />}
        {page === 'user-stats' && viewingUser && (
          <StatsView username={viewingUser} onBack={() => navigate('leaderboard')} />
        )}
        {page === 'how' && <HowItWorksView />}
        {page === 'profile' && (
          <ProfileView
            me={game.state.me}
            onChanged={() => game.reloadMe()}
            onSignOut={async () => {
              await api.logout();
              await game.reloadMe();
            }}
            onOpenAuth={() => setAuthOpen(true)}
          />
        )}
      </main>
      <footer className="ftr muted small">
        witladder · v{APP_VERSION} · climb the global ladder of wit
      </footer>
      {authOpen && (
        <AuthModal
          onClose={() => setAuthOpen(false)}
          onAuthed={() => game.reloadMe()}
        />
      )}
      <ConnectionLost connection={game.state.connection} />
      <Toasts toasts={game.state.toasts} />
    </div>
  );
}
